import { useState, useEffect, useRef, DependencyList, useCallback } from 'react'
import { nanoid } from 'nanoid'

import { isTRPCClientAbortError } from '@/utils/trpc'

type QueryFunction<TResult> = ({ signal }: { signal: AbortSignal }) => Promise<TResult>

type QueryOptions = { keepResultOnReload?: boolean; autoReload?: number }

export const useQueryExt = <TResult>(
    queryFunction: QueryFunction<TResult>,
    queryDeps: DependencyList,
    options?: QueryOptions
): {
    result: TResult | null
    updateResult: (result: TResult | null) => void
    isLoading: boolean
    reload: () => void
} => {
    const abortController = useRef<AbortController | null>(null)

    const [result, setResult] = useState<TResult | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [reloadToken, setReloadToken] = useState('')

    const keepResultOnReload = !!options?.keepResultOnReload
    const autoReload = options?.autoReload

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const query = useCallback(queryFunction, queryDeps)

    useEffect(() => {
        const doQuery = async () => {
            const currentAbortController = new AbortController()
            abortController.current = currentAbortController
            setIsLoading(true)

            try {
                const result = await query({ signal: currentAbortController.signal })
                setResult(result)
            } catch (error) {
                if (!isTRPCClientAbortError(error)) {
                    throw error
                }
            } finally {
                if (!currentAbortController.signal.aborted) {
                    setIsLoading(false)
                    abortController.current = null
                }
            }
        }

        if (!keepResultOnReload) {
            setResult(null)
        }

        doQuery()

        return () => {
            if (abortController.current) {
                abortController.current.abort()
                setIsLoading(false)
                abortController.current = null
            }
        }
    }, [query, reloadToken, keepResultOnReload])

    const reload = useCallback(() => {
        setReloadToken(nanoid())
    }, [])

    useEffect(() => {
        if (!autoReload) {
            return () => {}
        }

        const interval = setInterval(() => setReloadToken(nanoid()), autoReload)
        return () => clearInterval(interval)
    }, [autoReload])

    return { result, updateResult: setResult, isLoading, reload }
}

export const useQuery = <TResult>(queryFunction: QueryFunction<TResult>, queryDeps: DependencyList): TResult | null => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const { result } = useQueryExt(queryFunction, queryDeps)

    return result
}
